'use client';
import { useEffect, useRef } from 'react';
import { ETSY_URL, GUMROAD_URL, INFINITEMARKET_URL } from '@/data/bundles';

const included = [
  '43 Premium Resource Collections',
  'Canva & KDP Resources',
  'Stock Photos & Graphics',
  'Fonts & SVGs',
  'UI & Development Resources',
  'Business & Productivity Templates',
  'Digital Delivery',
];

const checkoutOptions = [
  { icon: '🛍️', name: 'Etsy', desc: 'Buy securely through Etsy', url: ETSY_URL, featured: true, badge: 'Most Popular' },
  { icon: '⚡', name: 'Gumroad', desc: 'Fast digital-product checkout', url: GUMROAD_URL, featured: false, badge: null },
  { icon: '🌐', name: 'InfiniteMarket', desc: 'Purchase directly through InfiniteMarket', url: INFINITEMARKET_URL, featured: false, badge: null },
];

export default function CheckoutSection() {
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('reveal-hidden');
            entry.target.classList.add('reveal-visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    itemRefs.current.forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="checkout"
      className="relative py-20 lg:py-28 overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #080B14 0%, #0F0A2E 50%, #080B14 100%)' }}
      aria-labelledby="checkout-heading"
    >
      {/* Blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="blob absolute w-96 h-96 bg-primary" style={{ top: '20%', left: '10%', opacity: 0.12 }} />
        <div className="blob blob-2 absolute w-80 h-80 bg-accent" style={{ bottom: '20%', right: '10%', opacity: 0.1 }} />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div
          className="text-center mb-14 reveal-hidden"
          ref={(el) => { itemRefs.current[0] = el; }}
        >
          <span
            className="inline-block px-4 py-1.5 rounded-full glass text-xs font-bold uppercase tracking-widest mb-4"
            style={{ color: 'var(--secondary-foreground)', border: '1px solid rgba(124,58,237,0.2)' }}
          >
            COMPLETE MEGA BUNDLE
          </span>
          <h2 id="checkout-heading" className="section-heading mb-4" style={{ color: 'var(--foreground)' }}>
            Get All 43 Premium Collections
          </h2>
          <div className="flex items-baseline justify-center gap-3 mb-2">
            <span className="text-6xl font-extrabold text-gradient">$79</span>
          </div>
          <p className="text-lg" style={{ color: 'var(--muted-foreground)' }}>One-Time Purchase</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-12">
          {/* What's Included */}
          <div
            className="lg:col-span-2 reveal-hidden"
            ref={(el) => { itemRefs.current[1] = el; }}
          >
            <div className="glass rounded-2xl p-6 h-full">
              <h3 className="font-bold text-lg mb-5" style={{ color: 'var(--foreground)' }}>What&apos;s Included</h3>
              <ul className="space-y-3">
                {included.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm" style={{ color: 'var(--foreground)' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width={18} height={18} style={{ color: 'var(--primary)', flexShrink: 0 }}>
                      <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Checkout Options */}
          <div
            className="lg:col-span-3 reveal-hidden"
            ref={(el) => { itemRefs.current[2] = el; }}
            style={{ transitionDelay: '100ms' }}
          >
            <p className="text-center text-sm mb-4 font-medium" style={{ color: 'var(--muted-foreground)' }}>Choose Your Preferred Checkout</p>
            <p className="text-center text-xs mb-6" style={{ color: 'var(--muted-foreground)' }}>Same complete bundle — choose the checkout platform you prefer.</p>
            <div className="flex flex-col gap-4">
              {checkoutOptions.map((opt, i) => (
                <a
                  key={i}
                  href={opt.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`checkout-card flex items-center gap-4 group no-underline ${opt.featured ? 'featured' : ''}`}
                  aria-label={`Buy on ${opt.name} for $79`}
                >
                  <div className="text-3xl flex-shrink-0" aria-hidden="true">{opt.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-bold text-base" style={{ color: 'var(--foreground)' }}>{opt.name}</span>
                      {opt.badge && (
                        <span
                          className="px-2 py-0.5 rounded-full text-xs font-bold text-white"
                          style={{ background: 'linear-gradient(135deg, rgb(79,70,229), rgb(124,58,237) 40%, rgb(236,72,153))' }}
                        >
                          {opt.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{opt.desc}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="font-extrabold text-lg" style={{ color: 'var(--foreground)' }}>$79</span>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width={18} height={18} className="group-hover:translate-x-1 transition-transform" style={{ color: 'var(--primary)' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div
          className="flex flex-wrap items-center justify-center gap-6 reveal-hidden"
          ref={(el) => { itemRefs.current[3] = el; }}
          style={{ transitionDelay: '200ms' }}
        >
          {['🔒 Secure Checkout', '⚡ Digital Product', '💳 Multiple Platforms', '♾️ One-Time Purchase'].map((badge) => (
            <div key={badge} className="flex items-center gap-2 text-sm" style={{ color: 'var(--muted-foreground)' }}>
              {badge}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
